import { useFetch } from "@hyper-fetch/react";
import { Link } from "@tanstack/react-router";
import { CalendarCheck, Loader2, MailCheck } from "lucide-react";
import { useEffect, useState } from "react";
import type { FormEvent } from "react";

import { figure, ghostButton, label, primaryButton, well } from "../../components/section";
import { siteVars } from "../../components/site-theme";
import { useSiteConfig } from "../../hooks/use-site-config";
import type { PublicSpecialist, SlotPick } from "../../types";
import { bookingPath } from "../../utils/booking-link";
import { openTimeLabelIn } from "../../utils/labels";
import { sdk } from "@/api/sdk";
import { DialogShell } from "@/components/layouts/shells/dialog-shell";
import { PanelQuestions } from "@/features/booking/modules/client-specialist/components/panel-questions";
import { cleanAnswers, missingAnswers } from "@/features/booking/modules/client-specialist/panel-form";
import type { ClientQuestion } from "@/features/booking/types/client.types";
import { ConsentCheck } from "@/features/legal/components/consent-check";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

type SlotGateDialogProps = {
  /** The time the visitor tapped, or `null` while the dialog is closed. */
  pick: SlotPick | null;
  specialist: PublicSpecialist | null;
  serviceName: string | null;
  onClose: () => void;
};

const field =
  "h-12 w-full rounded-2xl border-0 bg-[var(--site-well)] px-4 text-[15px] outline-none transition-shadow placeholder:text-muted-foreground focus-visible:shadow-[0_0_0_2px_var(--site-ink)]";

type Stage = { kind: "form"; error: string | null } | { kind: "sending" } | { kind: "sent"; email: string };

/** The API's own words when it has any, so "just taken" or "too many attempts" reach the person. */
const messageOf = (error: unknown) => {
  if (error && typeof error === "object" && "message" in error && typeof error.message === "string" && error.message) {
    return error.message;
  }
  return m.guest_book_error();
};

/**
 * Taking a time, without an account: a name, an email, and a link in the inbox. The way people
 * book a doctor online, and for the same reason - most people booking a session have no wish to
 * sign up for anything first.
 *
 * Nothing is booked here. The form asks the API to email a link; the booking happens when that
 * link is used, which proves the address belongs to the person who typed it. Until then the
 * trainer sees nothing, so a mistyped address or a prank costs one email and no slot.
 *
 * Signing in is still offered underneath, for people who already have an account and want the
 * visit to sit with the rest of their training.
 */
export const SlotGateDialog = ({ pick, specialist, serviceName, onClose }: SlotGateDialogProps) => {
  const site = useSiteConfig();
  const [stage, setStage] = useState<Stage>({ kind: "form", error: null });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [flagged, setFlagged] = useState<string[]>([]);
  const [healthConsent, setHealthConsent] = useState(false);
  const [consentMissing, setConsentMissing] = useState(false);

  // A new time starts a new form. The name and email stay: somebody picking a second time after
  // "just taken" should not have to type them again.
  useEffect(() => {
    if (pick) setStage({ kind: "form", error: null });
  }, [pick]);

  // Answers belong to one service, and the consent to what was answered: both start over with it.
  useEffect(() => {
    setAnswers({});
    setFlagged([]);
    setHealthConsent(false);
    setConsentMissing(false);
  }, [pick?.serviceId]);

  const organizationId = pick?.organizationId ?? site.organizationId;
  const when = pick && specialist ? openTimeLabelIn(pick.startAt, specialist.timeZone) : "";
  const redirect = pick ? bookingPath({ ...pick, organizationId: organizationId ?? undefined }) : undefined;
  // Without a business or a service there is nothing to book against; offer the sign-in path the
  // pack has always had, which can still work it out after login.
  const canBookAsGuest = Boolean(organizationId && pick?.serviceId);

  const questionRequest = sdk.bookingPublic.guest.questions.$get.setQueryParams({
    organizationId: organizationId ?? "",
    sessionTypeId: pick?.serviceId ?? "",
  });
  const questionsResponse = useFetch(questionRequest, { disabled: !pick || !canBookAsGuest });
  const questions: ClientQuestion[] = questionsResponse.data ?? [];
  const given = cleanAnswers(questions, answers);
  const hasAnswers = Object.keys(given).length > 0;

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!pick?.serviceId || !organizationId) return;
    const unanswered = missingAnswers(questions, answers);
    setFlagged(unanswered);
    // Answers can reveal health, so they need a tick of their own. Nothing ticked is ever assumed.
    setConsentMissing(hasAnswers && !healthConsent);
    if (unanswered.length > 0 || (hasAnswers && !healthConsent)) return;
    setStage({ kind: "sending" });
    const { error } = await sdk.bookingPublic.guest.$post.send({
      payload: {
        organizationId,
        sessionTypeId: pick.serviceId,
        specialistId: pick.specialistId,
        startAt: new Date(pick.startAt),
        name,
        email,
        phone,
        answers: given,
        healthDataConsent: hasAnswers && healthConsent,
      },
    });
    setStage(error ? { kind: "form", error: messageOf(error) } : { kind: "sent", email: email.trim() });
  };

  const sent = stage.kind === "sent";

  return (
    <DialogShell
      open={Boolean(pick && specialist)}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      icon={sent ? MailCheck : CalendarCheck}
      title={sent ? m.guest_book_sent_title() : m.landing_gate_title({ time: when })}
      description={
        sent
          ? m.guest_book_sent_body({ email: stage.email })
          : specialist
            ? m.landing_gate_body({ service: serviceName ?? m.landing_gate_visit(), name: specialist.name })
            : undefined
      }
    >
      {/* The dialog renders in a portal, outside the page wrapper, so it carries the tints itself. */}
      <div style={siteVars}>
        {sent ? (
          <div className="grid gap-4">
            <p className={label}>{m.guest_book_sent_spam()}</p>
            <button type="button" className={primaryButton} onClick={onClose}>
              {m.guest_book_sent_close()}
            </button>
          </div>
        ) : canBookAsGuest ? (
          <form className="grid gap-3" onSubmit={(event) => void submit(event)}>
            <label className="grid gap-1.5">
              <span className="text-[13px] font-medium">{m.guest_book_name()}</span>
              <input
                className={field}
                value={name}
                onChange={(event) => setName(event.target.value)}
                autoComplete="name"
                required
                minLength={2}
                maxLength={120}
              />
            </label>
            <label className="grid gap-1.5">
              <span className="text-[13px] font-medium">{m.guest_book_email()}</span>
              <input
                className={field}
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                autoComplete="email"
                required
                maxLength={255}
              />
            </label>
            <label className="grid gap-1.5">
              <span className="text-[13px] font-medium">
                {m.guest_book_phone()}{" "}
                <span className="font-normal text-muted-foreground">({m.guest_book_optional()})</span>
              </span>
              <input
                className={field}
                type="tel"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                autoComplete="tel"
                maxLength={40}
              />
            </label>

            {questions.length > 0 ? (
              <div className="grid gap-3">
                <PanelQuestions
                  questions={questions}
                  answers={answers}
                  onChange={(questionId, value) => setAnswers((current) => ({ ...current, [questionId]: value }))}
                  missing={flagged}
                />
                <ConsentCheck checked={healthConsent} onChange={setHealthConsent}>
                  {m.guest_book_health_consent()}
                </ConsentCheck>
                {consentMissing ? (
                  <p role="alert" className="text-destructive text-[13px]">
                    {m.guest_book_health_consent_required()}
                  </p>
                ) : null}
              </div>
            ) : null}

            {stage.kind === "form" && stage.error ? (
              <p role="alert" className="rounded-2xl bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {stage.error}
              </p>
            ) : null}

            <button type="submit" className={cn(primaryButton, "mt-1")} disabled={stage.kind === "sending"}>
              {stage.kind === "sending" ? (
                <>
                  <Loader2 aria-hidden className="size-4 animate-spin" />
                  {m.guest_book_submitting()}
                </>
              ) : (
                m.guest_book_submit()
              )}
            </button>
            <p className="text-center text-xs text-muted-foreground">
              {m.guest_book_terms()}{" "}
              <Link to="/terms" className="underline underline-offset-2">
                {m.footer_terms()}
              </Link>
              {" · "}
              <Link to="/privacy" className="underline underline-offset-2">
                {m.footer_privacy()}
              </Link>
            </p>
          </form>
        ) : null}

        {sent ? null : (
          <>
            <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground before:h-px before:flex-1 before:bg-[var(--site-dot)] after:h-px after:flex-1 after:bg-[var(--site-dot)]">
              {m.guest_book_have_account()}
            </div>
            <div className="grid gap-2">
              <Link to="/login" search={{ redirect }} className={ghostButton}>
                {m.guest_book_sign_in()}
              </Link>
              {site.phone ? (
                <a
                  href={site.phoneHref}
                  className={cn(well, "flex items-center justify-between gap-3 py-3 pr-4 pl-[18px]")}
                >
                  <small className={label}>{m.landing_gate_call()}</small>
                  <b className={cn(figure, "text-[17px]")}>{site.phone}</b>
                </a>
              ) : null}
            </div>
          </>
        )}
      </div>
    </DialogShell>
  );
};
